import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, Trash2, Database, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';

// Super-admin-only panel: one-click Reset (clear + seed) of the dedicated demo
// tenant, plus Clear-only and Seed-only. Requires typing the demo tenant name.
export default function DemoEnvironmentPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [profile, setProfile] = useState('balanced');

  const isSuper = user?.role === 'super_admin';

  const load = async () => {
    try { setStatus(await api.demoEnvStatus()); }
    catch (err) { toast.error(err.message || 'Could not load demo environment status'); }
  };
  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  if (!isSuper) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center" data-testid="demo-env-forbidden">
        <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <h1 className="text-lg font-semibold">Restricted</h1>
        <p className="text-sm text-muted-foreground mt-1">The Demo Environment panel is available to super admins only.</p>
      </div>
    );
  }

  const tenantName = status?.tenant_name || '';
  const confirmed = confirmText.trim() === tenantName || confirmText.trim() === status?.tenant_id;

  const run = async (action) => {
    if (!confirmed) { toast.error(`Type the demo tenant name to confirm: "${tenantName}"`); return; }
    setBusy(action); setResult(null);
    try {
      const fn = { reset: api.demoEnvReset, clear: api.demoEnvClear, seed: api.demoEnvSeed }[action];
      const res = action === 'clear' ? await fn(confirmText.trim()) : await fn(confirmText.trim(), profile);
      setResult({ action, ...res });
      toast.success(`Demo environment: ${action} complete`);
      setConfirmText('');
      load();
    } catch (err) {
      toast.error(err.message || `${action} failed`);
    } finally { setBusy(''); }
  };

  const renderSummary = (obj) => Object.entries(obj || {})
    .filter(([, v]) => typeof v === 'number' && v > 0)
    .map(([k, v]) => `${v} ${k}`).join(', ');

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="demo-env-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Demo Environment</h1>
          <p className="text-sm text-muted-foreground">Reset the demo tenant to a known, story-rich state before each demo.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="demo-env-tenant-banner">
        <Database className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p>Actions only ever touch the dedicated demo tenant <b>{tenantName || '…'}</b> (<span className="font-mono text-xs">{status?.tenant_id}</span>).
          The backbone (data sources, models, measures, glossary, users, roles) is never cleared.</p>
        </div>
      </div>

      {status && (
        <div className="rounded-2xl border border-border/40 bg-card/50 p-4" data-testid="demo-env-status">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium">Current demo-generated content</h2>
            <button onClick={load} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(status.clearable_counts || {}).length === 0 && (
              <span className="text-xs text-muted-foreground italic">Empty — nothing to clear.</span>
            )}
            {Object.entries(status.clearable_counts || {}).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-border/40 bg-muted/30 text-muted-foreground">
                {k} <b className="text-foreground">{v}</b>
              </span>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground/70 mt-2">
            Backbone: {status.backbone?.semantic_models} models · {status.backbone?.measures} measures · {status.backbone?.glossary} glossary terms
            {status.backbone?.present ? '' : ' — missing (Reset will recreate it)'}
          </p>
        </div>
      )}

      <div className="rounded-2xl border border-border/40 bg-card/50 p-4 space-y-3">
        <label className="text-sm font-medium block">Demo storyline</label>
        <div className="flex flex-wrap gap-2" data-testid="demo-env-profiles">
          {(status?.profiles || [{ id: 'balanced', label: 'Balanced book' }]).map(pf => (
            <button key={pf.id} onClick={() => setProfile(pf.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${profile === pf.id ? 'bg-primary/15 border-primary/50 text-primary' : 'bg-muted/20 border-border/40 text-muted-foreground hover:bg-muted/40'}`}
              data-testid={`demo-env-profile-${pf.id}`}>
              {pf.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground/70">Applied when you Reset or Seed. Clear-only ignores the storyline.</p>
        <label className="text-sm font-medium block pt-1">Type the demo tenant name to confirm</label>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={tenantName}
          className="rounded-lg bg-muted/50 border-border/50"
          data-testid="demo-env-confirm-input"
        />
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={() => run('reset')} disabled={!confirmed || !!busy}
            className="rounded-xl bg-primary text-primary-foreground gap-2" data-testid="demo-env-reset-btn">
            {busy === 'reset' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reset (clear + seed)
          </Button>
          <Button onClick={() => run('clear')} disabled={!confirmed || !!busy} variant="outline"
            className="rounded-xl gap-2 border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="demo-env-clear-btn">
            {busy === 'clear' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Clear only
          </Button>
          <Button onClick={() => run('seed')} disabled={!confirmed || !!busy} variant="outline"
            className="rounded-xl gap-2 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="demo-env-seed-btn">
            {busy === 'seed' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Seed only
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2" data-testid="demo-env-result">
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-300">
            <CheckCircle2 className="w-4 h-4" /> {result.action} complete
          </div>
          {result.backbone_created?.length > 0 && (
            <p className="text-xs text-muted-foreground">Recreated backbone: {result.backbone_created.join(', ')}.</p>
          )}
          {result.cleared && renderSummary(result.cleared) && (
            <p className="text-xs text-muted-foreground"><b>Cleared:</b> {renderSummary(result.cleared)}.</p>
          )}
          {result.created && renderSummary(result.created) && (
            <p className="text-xs text-muted-foreground"><b>Created:</b> {renderSummary(result.created)}.</p>
          )}
        </div>
      )}
    </div>
  );
}
